import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import {
  formatDateForDayView,
  getDayName,
  parseDateString,
  isHabitActiveForDate,
  calculateCurrentStreak,
  formatDateToString,
} from "../utils/dateUtils";
import {
  getDateDataFromState,
  selectCalendarDayViewData,
} from "../utils/dataUtils";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { useToastContext } from "./ToastContext";
import { useTheme } from "../hooks/useTheme";
import {
  Todo,
  Habit,
  Reminder,
  Note,
  TrackerEntry,
  Tracker,
  DailyHabitCompletion,
  JournalEntry,
} from "../types";
import {
  createTrackerEntry,
  deleteTrackerAsync,
} from "../store/slices/trackersSlice";
import {
  toggleTodo,
  updateTodo,
  deleteTodo,
  updateTodoAsync,
  deleteTodoAsync,
} from "../store/slices/todosSlice";
import {
  toggleHabitCompletion,
  createCompletion,
  deleteHabitById,
} from "../store/slices/habitsSlice";
import {
  updateReminderAsync,
  deleteReminderAsync,
} from "../store/slices/remindersSlice";
import { deleteNoteById } from "../store/slices/notesSlice";
import ConfirmationModal from "./ConfirmationModal";
import AppSidebar from "./AppSidebar";
import { BingoAnimation } from "../components/animations";

import {
  trackPageVisit,
  trackHabitEvent,
  trackTodoEvent,
  trackTrackerEvent,
  trackUserInteraction,
} from "../utils/analytics";

const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name) {
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  }
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const DayView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { date } = useParams<{ date: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { isDark, setTheme } = useTheme();

  const userProfile = useAppSelector((s) => s.user.profile);
  const initials = getInitials(userProfile?.name, userProfile?.username);

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // Parse the date parameter
  let selectedDate: Date;
  let dateString: string;
  if (date === "today") {
    selectedDate = new Date();
    dateString = formatDateToString(selectedDate);
  } else {
    selectedDate = date ? parseDateString(date) : new Date();
    dateString = date || formatDateToString(selectedDate);
  }
  const dayName = getDayName(selectedDate);
  const formattedDate = formatDateForDayView(selectedDate);

  // Check if the selected date is in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFutureDate = selectedDate > today;

  // Get Redux state - only the data needed for day view calculations
  const dayViewData = useAppSelector(selectCalendarDayViewData);

  // Get data for this date from Redux store
  const dateData = getDateDataFromState(dayViewData, dateString);

  // State for tracker entry form
  const [addingEntryFor, setAddingEntryFor] = useState<string | null>(null);
  const [entryValue, setEntryValue] = useState("");

  // State for todo editing
  const [editingTodoId, setEditingTodoId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPriority, setEditPriority] = useState<
    "low" | "medium" | "high" | undefined
  >();

  // State for reminder editing
  const [editingReminderId, setEditingReminderId] = useState<string | null>(
    null,
  );
  const [editReminderTitle, setEditReminderTitle] = useState("");
  const [editReminderTime, setEditReminderTime] = useState("");
  const [editReminderRecurring, setEditReminderRecurring] = useState<
    "daily" | "weekly" | "monthly" | ""
  >("");

  // State for confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(
    null,
  );

  // State for bingo animation
  const [bingoAnimation, setBingoAnimation] = useState<{
    type: "daily" | "weekly";
    show: boolean;
  } | null>(null);

  // Compute day progress for header badge
  const dayProgress = useMemo(() => {
    const activeHabits = dateData.habits.filter(
      (h: Habit) =>
        isHabitActiveForDate(h.activeForDays, selectedDate) && !h.isPaused,
    );
    const completedTodos = dateData.todos.filter(
      (td: Todo) => td.completed,
    ).length;
    const completedHabits = dateData.habitCompletions.filter(
      (c: DailyHabitCompletion) => c.completed,
    ).length;
    const total = dateData.todos.length + activeHabits.length;
    const completed = completedTodos + completedHabits;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, pct };
  }, [dateData, selectedDate]);

  useEffect(() => {
    trackPageVisit("day_view", {
      date: dateString,
      day_name: dayName,
      is_future_date: isFutureDate,
    });
  }, [dateString, dayName, isFutureDate]);

  const handleBack = () => {
    navigate("/app");
  };

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const getPriorityBadgeClass = (priority?: "low" | "medium" | "high") => {
    switch (priority) {
      case "high":
        return "bg-[#e23f5d]/15 text-[#e23f5d]";
      case "medium":
        return "bg-[#f5a623]/15 text-[#f5a623]";
      case "low":
        return "bg-[#22c67a]/15 text-[#22c67a]";
      default:
        return "bg-black/[0.06] dark:bg-white/[0.06] text-gray-500 dark:text-white/40";
    }
  };

  const mobileNavLinks = [
    { path: "/app", emoji: "\u{1F4C5}", label: t("nav.calendar") },
    { path: "/habits", emoji: "\u{1F525}", label: t("nav.habits") },
    { path: "/todos", emoji: "\u2705", label: t("nav.todos") },
    { path: "/trackers", emoji: "\u{1F4CA}", label: t("nav.trackers") },
    { path: "/notes", emoji: "\u{1F4DD}", label: t("nav.notes") },
  ];

  const getHabitCompletionStatus = (habitId: string) => {
    return (
      dateData.habitCompletions.find(
        (completion: DailyHabitCompletion) => completion.habitId === habitId,
      )?.completed || false
    );
  };

  const showConfirmationModal = (message: string, onConfirm: () => void) => {
    setModalMessage(message);
    setOnConfirmAction(() => onConfirm);
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (onConfirmAction) {
      onConfirmAction();
    }
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleAddTrackerEntry = (trackerId: string) => {
    // Don't allow adding entries for future dates
    if (isFutureDate) return;
    setAddingEntryFor(trackerId);
    setEntryValue("");
  };

  const handleSaveTrackerEntry = async (trackerId: string) => {
    // Don't allow saving entries for future dates
    if (isFutureDate) return;

    if (!entryValue.trim()) return;

    const tracker = dateData.trackers.find((t: Tracker) => t.id === trackerId);
    if (!tracker) return;

    // Convert value based on data type
    let processedValue: string | number | boolean = entryValue.trim();

    if (tracker.dataType === "number") {
      const numValue = parseFloat(entryValue);
      if (isNaN(numValue)) return;
      processedValue = numValue;
    } else if (tracker.dataType === "boolean") {
      processedValue = entryValue.toLowerCase() === "yes";
    }
    // For time and options, keep as string
    // time comes as HH:MM format
    // options comes as the selected option string

    const result = await dispatch(
      createTrackerEntry({
        trackerId,
        date: dateString,
        value: processedValue,
      }),
    ).unwrap();

    // Show toast if points earned
    if (result.pointsEarned > 0) {
      addToast(
        t("toast.pointsEarned", { points: result.pointsEarned }),
        "success",
      );
    }

    // Track tracker entry event with tracker metadata
    trackTrackerEvent(tracker.name, processedValue, userProfile?.id, {
      tracker_id: trackerId,
      date: dateString,
      data_type: tracker.dataType,
    });

    // Keep legacy interaction analytics for existing dashboards
    trackUserInteraction(
      "create_tracker_entry",
      "tracker_management",
      trackerId,
    );

    if (tracker.dataType !== "number") {
      setAddingEntryFor(null);
    }
    setEntryValue("");
  };

  const handleCancelAddEntry = () => {
    setAddingEntryFor(null);
    setEntryValue("");
  };

  const handleToggleTodo = async (
    todoId: string,
    currentCompleted: boolean,
  ) => {
    const newCompleted = !currentCompleted;
    // Toggle locally first for immediate UI feedback
    dispatch(toggleTodo(todoId));

    try {
      // Update on backend and get the result
      const result = await dispatch(
        updateTodoAsync({ id: todoId, updates: { completed: newCompleted } }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Check for bingo rewards
      if (result.dailyBingoAwarded) {
        setBingoAnimation({ type: "daily", show: true });
      } else if (result.weeklyBingoAwarded) {
        setBingoAnimation({ type: "weekly", show: true });
      }
    } catch (error) {
      console.error("Failed to update todo:", error);
      // Revert local change on error
      dispatch(toggleTodo(todoId));
    }

    // Track todo completion event
    trackTodoEvent(
      newCompleted ? "todo_completed" : "todo_uncompleted",
      todoId,
      {
        date: dateString,
        day_name: dayName,
      },
    );
  };

  const handleToggleHabit = async (habitId: string) => {
    // Don't allow toggling for future dates
    if (isFutureDate) return;

    // Toggle locally first for immediate UI feedback
    dispatch(toggleHabitCompletion({ habitId, date: dateString }));

    try {
      // Update on backend and get the result
      const result = await dispatch(
        createCompletion({ habitId, date: dateString }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Check for bingo rewards
      if (result.dailyBingoAwarded) {
        setBingoAnimation({ type: "daily", show: true });
      } else if (result.weeklyBingoAwarded) {
        setBingoAnimation({ type: "weekly", show: true });
      }
    } catch (error) {
      console.error("Failed to create habit completion:", error);
      // Revert local change on error
      dispatch(toggleHabitCompletion({ habitId, date: dateString }));
    }

    // Track habit completion event
    trackHabitEvent("habit_completed", habitId, {
      date: dateString,
      day_name: dayName,
    });
  };

  const handleEditTodo = (todo: Todo) => {
    setEditingTodoId(todo.id);
    setEditTitle(todo.title);
    setEditPriority(todo.priority);
  };

  const handleSaveEdit = (todoId: string) => {
    if (!editTitle.trim()) return;

    const updates: Partial<Todo> = {
      title: editTitle.trim(),
      priority: editPriority,
    };

    // Update locally first for immediate UI feedback
    dispatch(updateTodo({ id: todoId, updates }));
    // Then update on backend
    dispatch(updateTodoAsync({ id: todoId, updates }));

    // Track todo editing event
    trackUserInteraction("edit_todo", "todo_management", todoId);

    // Reset edit state
    setEditingTodoId(null);
    setEditTitle("");
    setEditPriority(undefined);
  };

  const handleCancelEdit = () => {
    setEditingTodoId(null);
    setEditTitle("");
    setEditPriority(undefined);
  };

  const handleDeleteTodo = (todoId: string) => {
    showConfirmationModal(t("dayView.confirmDeleteTodo"), () => {
      // Delete locally first for immediate UI feedback
      dispatch(deleteTodo(todoId));
      // Then delete on backend
      dispatch(deleteTodoAsync(todoId));

      // Track todo deletion event
      trackUserInteraction("delete_todo", "todo_management", todoId);
    });
  };

  const handleEditReminder = (reminder: Reminder) => {
    setEditingReminderId(reminder.id);
    setEditReminderTitle(reminder.title);
    setEditReminderTime(reminder.time || "");
    setEditReminderRecurring(reminder.recurring || "");
  };

  const handleSaveEditReminder = (reminderId: string) => {
    if (!editReminderTitle.trim()) return;

    const updates: Partial<Reminder> = {
      title: editReminderTitle.trim(),
      time: editReminderTime || undefined,
      recurring: editReminderRecurring || undefined,
    };

    // Update on backend
    dispatch(updateReminderAsync({ id: reminderId, updates }));

    // Track reminder editing event
    trackUserInteraction("edit_reminder", "reminder_management", reminderId);

    // Reset edit state
    setEditingReminderId(null);
    setEditReminderTitle("");
    setEditReminderTime("");
    setEditReminderRecurring("");
  };

  const handleCancelEditReminder = () => {
    setEditingReminderId(null);
    setEditReminderTitle("");
    setEditReminderTime("");
    setEditReminderRecurring("");
  };

  const handleDeleteReminder = (reminderId: string) => {
    showConfirmationModal(t("dayView.confirmDeleteReminder"), () => {
      // Delete on backend
      dispatch(deleteReminderAsync(reminderId));

      // Track reminder deletion event
      trackUserInteraction(
        "delete_reminder",
        "reminder_management",
        reminderId,
      );
    });
  };

  const handleDeleteNote = (noteId: string) => {
    showConfirmationModal(t("dayView.confirmDeleteNote"), () => {
      // Delete on backend
      dispatch(deleteNoteById(noteId));

      // Track note deletion event
      trackUserInteraction("delete_note", "note_management", noteId);
    });
  };

  const handleEditHabit = (habitId: string) => {
    navigate(`/edit-habit/${habitId}`);
  };

  const handleDeleteHabit = (habitId: string) => {
    showConfirmationModal(t("dayView.confirmDeleteHabit"), () => {
      // Delete locally first for immediate UI feedback
      dispatch(deleteHabitById(habitId));

      // Track habit deletion event
      trackUserInteraction("delete_habit", "habit_management", habitId);
    });
  };

  const handleDeleteTracker = (trackerId: string) => {
    showConfirmationModal(t("dayView.confirmDeleteTracker"), () => {
      // Delete via API
      dispatch(deleteTrackerAsync(trackerId));

      // Track tracker deletion event
      trackUserInteraction("delete_tracker", "tracker_management", trackerId);
    });
  };

  return (
    <>
      <MetaHead
        title={t("dayView.title")}
        description={t("dayView.description") as string}
        keywords={t("dayView.keywords") as string}
      />

      {/* â”€â”€ ROOT LAYOUT â”€â”€ */}
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* â”€â”€ DESKTOP SIDEBAR â”€â”€ */}
        <AppSidebar showQuickActions={true} />

        {/* â”€â”€ MAIN CONTENT â”€â”€ */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* â”€â”€ MOBILE TOPBAR â”€â”€ */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleBack}
              aria-label={t("dayView.goBackToCalendar") as string}
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div className="text-center">
              <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
                {dayName}, {formattedDate}
              </p>
              <p className="text-[17px] text-gray-400 dark:text-white/35 mt-0.5">
                {selectedDate.getFullYear()}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "\u2600\uFE0F" : "\u{1F319}"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* â”€â”€ DESKTOP PAGE HEADER â”€â”€ */}
          <div className="hidden lg:flex items-center justify-between px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                aria-label={t("dayView.goBackToCalendar") as string}
                className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 3L5 8l5 5"
                    stroke="#7b9fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
                  {dayName}, {formattedDate}
                </h1>
                <p className="text-xs text-gray-400 dark:text-white/30 mt-0.5">
                  {selectedDate.getFullYear()}
                </p>
              </div>
            </div>
            {dayProgress.total > 0 && (
              <div className="flex items-center gap-2 bg-black/[0.04] dark:bg-white/[0.04] border border-black/[0.07] dark:border-white/[0.07] rounded-xl px-4 py-2">
                <span className="text-[18px] font-medium text-gray-400 dark:text-white/40">
                  {t("dayView.todaysProgress")}
                </span>
                <span className="text-[19px] font-semibold text-[#f5a623]">
                  {dayProgress.completed} / {dayProgress.total}
                </span>
              </div>
            )}
          </div>

          {/* -- MOBILE QUICK ACTIONS (hidden on lg) -- */}
          <div className="lg:hidden border-t border-black/[0.07] dark:border-white/[0.07]">
            <p className="text-[15px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/20 px-4 pt-2 pb-1.5">
              {t("quickActions.title")}
            </p>
            <div className="grid grid-cols-5 gap-2 px-3 pb-3">
              <button
                onClick={() => navigate(`/add-todo?date=${dateString}`)}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect
                      x="1"
                      y="2"
                      width="14"
                      height="2"
                      rx="1"
                      fill="#378ADD"
                    />
                    <rect
                      x="1"
                      y="7"
                      width="10"
                      height="2"
                      rx="1"
                      fill="#378ADD"
                    />
                    <rect
                      x="1"
                      y="12"
                      width="12"
                      height="2"
                      rx="1"
                      fill="#378ADD"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.todo")}
                </span>
              </button>
              <button
                onClick={() => navigate("/add-habit")}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#f5a623]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="6"
                      stroke="#f5a623"
                      strokeWidth="2"
                    />
                    <path
                      d="M5 8l2 2 4-4"
                      stroke="#f5a623"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.habit")}
                </span>
              </button>
              <button
                onClick={() => navigate(`/add-reminder?date=${dateString}`)}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#e23f5d]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 2a4 4 0 0 0-4 4v3l-1 1h10l-1-1V6a4 4 0 0 0-4-4z"
                      fill="#E23F5D"
                    />
                    <rect
                      x="6.5"
                      y="12"
                      width="3"
                      height="1.5"
                      rx="0.75"
                      fill="#E23F5D"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.remind")}
                </span>
              </button>
              <button
                onClick={() => navigate(`/add-note?date=${dateString}`)}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#22c67a]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect
                      x="2"
                      y="1"
                      width="12"
                      height="14"
                      rx="2"
                      stroke="#22c67a"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M5 5h6M5 8h4M5 11h5"
                      stroke="#22c67a"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.note")}
                </span>
              </button>
              <button
                onClick={() => navigate("/add-tracker")}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#a33fe8]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2 12l3-4 3 2 3-5 3 3"
                      stroke="#a33fe8"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.track")}
                </span>
              </button>
            </div>
          </div>

          {/* â”€â”€ SCROLL AREA â”€â”€ */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4 space-y-3 lg:space-y-4">
            {/* â”€â”€ PROGRESS BAR â”€â”€ */}
            {dayProgress.total > 0 && (
              <div className="animate-slide-up rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] px-4 py-3.5">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[18px] font-medium text-gray-500 dark:text-white/50">
                    {t("dayView.todaysProgress")}
                  </span>
                  <span className="text-[18px] font-semibold text-[#f5a623]">
                    {dayProgress.pct}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-black/[0.06] dark:bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#f5a623] to-[#e87b3f] transition-all duration-500 ease-out"
                    style={{ width: `${dayProgress.pct}%` }}
                  />
                </div>
                <div className="flex items-center gap-4 mt-2.5">
                  {dateData.todos.length > 0 && (
                    <div className="flex items-center gap-1.5 text-[16px] text-gray-400 dark:text-white/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3b5cde]" />
                      {t("dayView.todos")}{" "}
                      {dateData.todos.filter((td: Todo) => td.completed).length}
                      /{dateData.todos.length}
                    </div>
                  )}
                  {dateData.habits.filter((h: Habit) => !h.isPaused).length >
                    0 && (
                    <div className="flex items-center gap-1.5 text-[16px] text-gray-400 dark:text-white/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f5a623]" />
                      {t("dayView.habits")}{" "}
                      {
                        dateData.habitCompletions.filter(
                          (c: DailyHabitCompletion) => c.completed,
                        ).length
                      }
                      /
                      {dateData.habits.filter((h: Habit) => !h.isPaused).length}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* â”€â”€ TODOS â”€â”€ */}
            {dateData.todos.length > 0 && (
              <section className="animate-slide-up">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#3b5cde]" />
                    <span className="text-[18px] font-medium uppercase tracking-wider text-gray-400 dark:text-white/30">
                      {t("dayView.todos")}
                    </span>
                    <span className="text-[17px] font-medium text-gray-300 dark:text-white/20">
                      {dateData.todos.filter((td: Todo) => td.completed).length}{" "}
                      / {dateData.todos.length}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/add-todo?date=${dateString}`)}
                    className="tap-scale flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#3b5cde]/15 border border-[#3b5cde]/25 text-[17px] font-medium text-[#7b9fff] hover:bg-[#3b5cde]/25 transition-all"
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 1v10M1 6h10"
                        stroke="#7b9fff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                    {t("dayView.add")}
                  </button>
                </div>

                <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] overflow-hidden divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                  {dateData.todos.map((todo: Todo) => (
                    <div key={todo.id} className="group">
                      {editingTodoId === todo.id ? (
                        <div className="px-4 py-3 space-y-2.5">
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full px-3 py-2 text-[17px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-black/[0.03] dark:bg-white/[0.05] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#f5a623]/50"
                            placeholder={
                              t("dayView.todoTitlePlaceholder") as string
                            }
                            autoFocus
                          />
                          <div className="flex items-center gap-2 flex-wrap">
                            <select
                              value={editPriority || ""}
                              onChange={(e) =>
                                setEditPriority(
                                  (e.target.value as
                                    | "low"
                                    | "medium"
                                    | "high") || undefined,
                                )
                              }
                              className="px-2.5 py-1.5 text-[16px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-white dark:bg-white/[0.05] text-gray-700 dark:text-white/70 focus:outline-none"
                            >
                              <option value="">
                                {t("dayView.noPriority")}
                              </option>
                              <option value="low">{t("dayView.low")}</option>
                              <option value="medium">
                                {t("dayView.medium")}
                              </option>
                              <option value="high">{t("dayView.high")}</option>
                            </select>
                            <button
                              onClick={() => handleSaveEdit(todo.id)}
                              className="px-3 py-1.5 text-[16px] font-medium bg-[#f5a623] text-[#1a1300] rounded-lg hover:opacity-90 transition-all"
                            >
                              {t("dayView.save")}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1.5 text-[16px] font-medium border border-black/[0.1] dark:border-white/[0.1] text-gray-500 dark:text-white/50 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-all"
                            >
                              {t("dayView.cancel")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors">
                          <input
                            type="checkbox"
                            checked={todo.completed}
                            onChange={() =>
                              handleToggleTodo(todo.id, todo.completed)
                            }
                            className="w-4 h-4 rounded flex-shrink-0 cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={`text-[17px] block truncate ${todo.completed ? "line-through text-gray-400 dark:text-white/30" : "text-gray-800 dark:text-white/80"}`}
                              >
                                {todo.title}
                              </span>
                              {todo.priority && (
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[13px] font-semibold flex-shrink-0 ${getPriorityBadgeClass(todo.priority)}`}
                                >
                                  {todo.priority}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEditTodo(todo)}
                              title={t("dayView.editTodo") as string}
                              className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M10 2l4 4-8 8H2v-4l8-8z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteTodo(todo.id)}
                              title={t("dayView.deleteTodo") as string}
                              className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-[#e23f5d] hover:bg-[#e23f5d]/10 transition-all"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M2 4h12M6 4V2h4v2M5 4v8a1 1 0 001 1h4a1 1 0 001-1V4"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* â”€â”€ HABITS â”€â”€ */}
            {(() => {
              const activeHabits = dateData.habits.filter(
                (habit: Habit) =>
                  isHabitActiveForDate(habit.activeForDays, selectedDate) &&
                  !habit.isPaused,
              );
              if (activeHabits.length === 0) return null;
              const completedCount = activeHabits.filter((h: Habit) =>
                getHabitCompletionStatus(h.id),
              ).length;
              return (
                <section className="animate-slide-up">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#f5a623]" />
                      <span className="text-[18px] font-medium uppercase tracking-wider text-gray-400 dark:text-white/30">
                        {t("dayView.habits")}
                      </span>
                      <span className="text-[17px] font-medium text-gray-300 dark:text-white/20">
                        {completedCount} / {activeHabits.length}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate("/add-habit")}
                      className="tap-scale flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#f5a623]/12 border border-[#f5a623]/20 text-[17px] font-medium text-[#f5a623] hover:bg-[#f5a623]/20 transition-all"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M6 1v10M1 6h10"
                          stroke="#f5a623"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                      {t("dayView.add")}
                    </button>
                  </div>

                  <div
                    className={`rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] overflow-hidden divide-y divide-black/[0.05] dark:divide-white/[0.05] ${isFutureDate ? "opacity-70" : ""}`}
                  >
                    {activeHabits.map((habit: Habit) => {
                      const isCompleted = getHabitCompletionStatus(habit.id);
                      const habitStreak = calculateCurrentStreak(
                        {
                          activeForDays: habit.activeForDays,
                          createdAt: habit.createdAt,
                        },
                        dayViewData.habits.completions.filter(
                          (c: any) => c.habitId === habit.id,
                        ),
                      );
                      return (
                        <div
                          key={habit.id}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors group"
                        >
                          <input
                            type="checkbox"
                            checked={isCompleted}
                            onChange={() => handleToggleHabit(habit.id)}
                            disabled={isFutureDate}
                            className={`w-4 h-4 rounded flex-shrink-0 ${isFutureDate ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                          />
                          <div className="flex-1 min-w-0">
                            <span
                              className={`text-[17px] block truncate ${isCompleted ? "line-through text-gray-400 dark:text-white/30" : isFutureDate ? "text-gray-400 dark:text-white/40" : "text-gray-800 dark:text-white/80"}`}
                            >
                              {habit.name}
                            </span>
                            {habitStreak > 0 && (
                              <span className="text-[14px] text-gray-400 dark:text-white/20 mt-0.5 block">
                                {"\u{1F525}"} {t("dayView.streak")}:{" "}
                                {habitStreak}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => navigate(`/habit/${habit.id}`)}
                              title={t("dayView.viewDetails") as string}
                              className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <circle
                                  cx="8"
                                  cy="8"
                                  r="6"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                />
                                <path
                                  d="M8 7v4M8 5.5v.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleEditHabit(habit.id)}
                              title={t("dayView.editHabit") as string}
                              className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M10 2l4 4-8 8H2v-4l8-8z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteHabit(habit.id)}
                              title={t("dayView.deleteHabit") as string}
                              className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-[#e23f5d] hover:bg-[#e23f5d]/10 transition-all"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M2 4h12M6 4V2h4v2M5 4v8a1 1 0 001 1h4a1 1 0 001-1V4"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })()}

            {/* â”€â”€ JOURNAL â”€â”€ */}
            {(dateData.journal.length > 0 || !isFutureDate) && (
              <section className="animate-slide-up">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#e87b3f]" />
                    <span className="text-[18px] font-medium uppercase tracking-wider text-gray-400 dark:text-white/30">
                      {t("dayView.journalEntry")}
                    </span>
                  </div>
                  {dateData.journal.length === 0 && (
                    <button
                      onClick={() =>
                        navigate(`/add-journal-entry?date=${dateString}`)
                      }
                      className="tap-scale flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#e87b3f]/12 border border-[#e87b3f]/20 text-[17px] font-medium text-[#e87b3f] hover:bg-[#e87b3f]/20 transition-all"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M6 1v10M1 6h10"
                          stroke="#e87b3f"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                      {t("dayView.add")}
                    </button>
                  )}
                </div>

                <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] overflow-hidden">
                  {dateData.journal.length > 0 ? (
                    dateData.journal.map((entry: JournalEntry) => (
                      <div
                        key={entry.id}
                        className="flex items-start gap-3 px-4 py-3.5 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors group"
                      >
                        <div className="w-7 h-7 rounded-lg bg-[#e87b3f]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <rect
                              x="2"
                              y="1"
                              width="12"
                              height="14"
                              rx="2"
                              stroke="#e87b3f"
                              strokeWidth="1.8"
                            />
                            <path
                              d="M5 5h6M5 8h4M5 11h5"
                              stroke="#e87b3f"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[17px] font-medium text-gray-800 dark:text-white/80 leading-none">
                            {entry.title}
                          </p>
                          <p className="text-[15px] text-gray-500 dark:text-white/30 mt-1.5 leading-relaxed line-clamp-2">
                            {entry.content.length > 120
                              ? `${entry.content.substring(0, 120)}...`
                              : entry.content}
                          </p>
                          {entry.mood && (
                            <span className="inline-block mt-1.5 px-1.5 py-0.5 rounded text-[13px] font-medium bg-[#e87b3f]/10 text-[#e87b3f] capitalize">
                              {entry.mood}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() =>
                            navigate(`/journal-details/${dateString}`)
                          }
                          title={t("dayView.viewJournalDetails") as string}
                          className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all opacity-0 group-hover:opacity-100 flex-shrink-0 mt-0.5"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <ellipse
                              cx="8"
                              cy="8"
                              rx="6"
                              ry="4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                            <circle cx="8" cy="8" r="2" fill="currentColor" />
                          </svg>
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-4 text-center">
                      <p className="text-[16px] text-gray-400 dark:text-white/30">
                        {t("dayView.noJournalEntry")}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* â”€â”€ REMINDERS â”€â”€ */}
            {dateData.reminders.length > 0 && (
              <section className="animate-slide-up">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#e23f5d]" />
                    <span className="text-[18px] font-medium uppercase tracking-wider text-gray-400 dark:text-white/30">
                      {t("dayView.reminders")}
                    </span>
                    <span className="text-[17px] font-medium text-gray-300 dark:text-white/20">
                      {dateData.reminders.length}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/add-reminder?date=${dateString}`)}
                    className="tap-scale flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#e23f5d]/12 border border-[#e23f5d]/20 text-[17px] font-medium text-[#e23f5d] hover:bg-[#e23f5d]/20 transition-all"
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 1v10M1 6h10"
                        stroke="#e23f5d"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                    {t("dayView.add")}
                  </button>
                </div>

                <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] overflow-hidden divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                  {dateData.reminders.map((reminder: Reminder) => (
                    <div key={reminder.id} className="group">
                      {editingReminderId === reminder.id ? (
                        <div className="px-4 py-3 space-y-2.5">
                          <input
                            type="text"
                            value={editReminderTitle}
                            onChange={(e) =>
                              setEditReminderTitle(e.target.value)
                            }
                            className="w-full px-3 py-2 text-[17px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-black/[0.03] dark:bg-white/[0.05] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#e23f5d]/50"
                            placeholder={
                              t("dayView.reminderTitlePlaceholder") as string
                            }
                            autoFocus
                          />
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="time"
                              value={editReminderTime}
                              onChange={(e) =>
                                setEditReminderTime(e.target.value)
                              }
                              className="px-2.5 py-1.5 text-[16px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-white dark:bg-white/[0.05] text-gray-700 dark:text-white/70 focus:outline-none"
                            />
                            <select
                              value={editReminderRecurring}
                              onChange={(e) =>
                                setEditReminderRecurring(
                                  e.target.value as
                                    | "daily"
                                    | "weekly"
                                    | "monthly"
                                    | "",
                                )
                              }
                              className="px-2.5 py-1.5 text-[16px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-white dark:bg-white/[0.05] text-gray-700 dark:text-white/70 focus:outline-none"
                            >
                              <option value="">{t("dayView.oneTime")}</option>
                              <option value="daily">
                                {t("dayView.daily")}
                              </option>
                              <option value="weekly">
                                {t("dayView.weekly")}
                              </option>
                              <option value="monthly">
                                {t("dayView.monthly")}
                              </option>
                            </select>
                            <button
                              onClick={() =>
                                handleSaveEditReminder(reminder.id)
                              }
                              className="px-3 py-1.5 text-[16px] font-medium bg-[#f5a623] text-[#1a1300] rounded-lg hover:opacity-90 transition-all"
                            >
                              {t("dayView.save")}
                            </button>
                            <button
                              onClick={handleCancelEditReminder}
                              className="px-3 py-1.5 text-[16px] font-medium border border-black/[0.1] dark:border-white/[0.1] text-gray-500 dark:text-white/50 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-all"
                            >
                              {t("dayView.cancel")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors">
                          <div className="w-7 h-7 rounded-lg bg-[#e23f5d]/20 flex items-center justify-center flex-shrink-0">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M8 2a4 4 0 0 0-4 4v3l-1 1h10l-1-1V6a4 4 0 0 0-4-4z"
                                fill="#E23F5D"
                              />
                              <rect
                                x="6.5"
                                y="12"
                                width="3"
                                height="1.5"
                                rx="0.75"
                                fill="#E23F5D"
                              />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-[17px] text-gray-800 dark:text-white/80 block truncate">
                              {reminder.title}
                            </span>
                            <div className="flex items-center gap-2 mt-0.5">
                              {reminder.time && (
                                <span className="text-[14px] text-gray-400 dark:text-white/25">
                                  ðŸ• {reminder.time}
                                </span>
                              )}
                              {reminder.recurring && (
                                <span className="px-1.5 py-0.5 rounded text-[13px] font-medium bg-black/[0.06] dark:bg-white/[0.06] text-gray-500 dark:text-white/30">
                                  {reminder.recurring}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEditReminder(reminder)}
                              title={t("dayView.editReminder") as string}
                              className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M10 2l4 4-8 8H2v-4l8-8z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteReminder(reminder.id)}
                              title={t("dayView.deleteReminder") as string}
                              className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-[#e23f5d] hover:bg-[#e23f5d]/10 transition-all"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M2 4h12M6 4V2h4v2M5 4v8a1 1 0 001 1h4a1 1 0 001-1V4"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* â”€â”€ NOTES â”€â”€ */}
            {dateData.notes.length > 0 && (
              <section className="animate-slide-up">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#22c67a]" />
                    <span className="text-[18px] font-medium uppercase tracking-wider text-gray-400 dark:text-white/30">
                      {t("dayView.notes")}
                    </span>
                    <span className="text-[17px] font-medium text-gray-300 dark:text-white/20">
                      {dateData.notes.length}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/add-note?date=${dateString}`)}
                    className="tap-scale flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#22c67a]/12 border border-[#22c67a]/20 text-[17px] font-medium text-[#22c67a] hover:bg-[#22c67a]/20 transition-all"
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 1v10M1 6h10"
                        stroke="#22c67a"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                    {t("dayView.add")}
                  </button>
                </div>

                <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] overflow-hidden divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                  {dateData.notes.map((note: Note) => (
                    <div
                      key={note.id}
                      className="flex items-start gap-3 px-4 py-3.5 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-[#22c67a]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <rect
                            x="2"
                            y="1"
                            width="12"
                            height="14"
                            rx="2"
                            stroke="#22c67a"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M5 5h6M5 8h4M5 11h5"
                            stroke="#22c67a"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[17px] font-medium text-gray-800 dark:text-white/80 leading-none">
                          {note.title}
                        </p>
                        {note.content && (
                          <p className="text-[15px] text-gray-500 dark:text-white/30 mt-1.5 leading-relaxed line-clamp-2">
                            {note.content}
                          </p>
                        )}
                        {note.tags && note.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {note.tags.map((tag: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-1.5 py-0.5 rounded text-[13px] font-medium bg-[#22c67a]/10 text-[#22c67a]"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5">
                        <button
                          onClick={() => navigate(`/note/${note.id}`)}
                          title={t("dayView.viewNoteDetails") as string}
                          className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <ellipse
                              cx="8"
                              cy="8"
                              rx="6"
                              ry="4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                            <circle cx="8" cy="8" r="2" fill="currentColor" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          title={t("dayView.deleteNote") as string}
                          className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-[#e23f5d] hover:bg-[#e23f5d]/10 transition-all"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M2 4h12M6 4V2h4v2M5 4v8a1 1 0 001 1h4a1 1 0 001-1V4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* â”€â”€ TRACKERS â”€â”€ */}
            {dateData.trackers.filter((tr: Tracker) => !tr.isPaused).length >
              0 && (
              <section
                className={`animate-slide-up ${isFutureDate ? "opacity-70" : ""}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#a33fe8]" />
                    <span className="text-[18px] font-medium uppercase tracking-wider text-gray-400 dark:text-white/30">
                      {t("dayView.trackers")}
                    </span>
                    <span className="text-[17px] font-medium text-gray-300 dark:text-white/20">
                      {dateData.trackerEntries.length}/
                      {
                        dateData.trackers.filter((tr: Tracker) => !tr.isPaused)
                          .length
                      }
                    </span>
                    {isFutureDate && (
                      <span className="text-[15px] text-gray-400 dark:text-white/25">
                        {t("dayView.futureDateDisabled")}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => navigate("/add-tracker")}
                    className="tap-scale flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#a33fe8]/12 border border-[#a33fe8]/20 text-[17px] font-medium text-[#a33fe8] hover:bg-[#a33fe8]/20 transition-all"
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 1v10M1 6h10"
                        stroke="#a33fe8"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                    {t("dayView.add")}
                  </button>
                </div>

                <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] overflow-hidden divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                  {dateData.trackers
                    .filter((tr: Tracker) => !tr.isPaused)
                    .map((tracker: Tracker) => {
                      const entries = dateData.trackerEntries.filter(
                        (e: TrackerEntry) => e.trackerId === tracker.id,
                      );
                      const hasEntries = entries.length > 0;
                      return (
                        <div key={tracker.id} className="group">
                          <div className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors">
                            <div className="w-7 h-7 rounded-lg bg-[#a33fe8]/20 flex items-center justify-center flex-shrink-0">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <rect
                                  x="2"
                                  y="8"
                                  width="3"
                                  height="6"
                                  rx="0.8"
                                  fill="#a33fe8"
                                />
                                <rect
                                  x="6.5"
                                  y="5"
                                  width="3"
                                  height="9"
                                  rx="0.8"
                                  fill="#a33fe8"
                                />
                                <rect
                                  x="11"
                                  y="2"
                                  width="3"
                                  height="12"
                                  rx="0.8"
                                  fill="#a33fe8"
                                />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[17px] text-gray-800 dark:text-white/80 truncate">
                                  {tracker.name}
                                </span>
                                <span className="px-1.5 py-0.5 rounded text-[13px] font-medium bg-[#a33fe8]/10 text-[#a33fe8] flex-shrink-0 capitalize">
                                  {tracker.dataType}
                                </span>
                                {tracker.unit && (
                                  <span className="text-[14px] text-gray-400 dark:text-white/25 flex-shrink-0">
                                    {tracker.unit}
                                  </span>
                                )}
                              </div>
                              {hasEntries && (
                                <div className="mt-0.5">
                                  {tracker.dataType === "number" ? (
                                    <span className="text-[15px] text-gray-500 dark:text-white/30">
                                      {entries
                                        .map((e: TrackerEntry) => e.value)
                                        .join(", ")}{" "}
                                      {tracker.unit || ""}
                                    </span>
                                  ) : (
                                    <span className="text-[15px] text-gray-500 dark:text-white/30">
                                      {tracker.dataType === "boolean"
                                        ? entries[0].value
                                          ? t("dayView.yes")
                                          : t("dayView.no")
                                        : String(entries[0].value)}
                                      {tracker.unit ? ` ${tracker.unit}` : ""}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() =>
                                  navigate(`/tracker/${tracker.id}`)
                                }
                                title={
                                  t("dayView.viewTrackerDetails") as string
                                }
                                className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all"
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <ellipse
                                    cx="8"
                                    cy="8"
                                    rx="6"
                                    ry="4"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  />
                                  <circle
                                    cx="8"
                                    cy="8"
                                    r="2"
                                    fill="currentColor"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() =>
                                  navigate(`/edit-tracker/${tracker.id}`)
                                }
                                title={t("dayView.editTracker") as string}
                                className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-gray-600 dark:hover:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] transition-all"
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M10 2l4 4-8 8H2v-4l8-8z"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteTracker(tracker.id)}
                                title={t("dayView.deleteTracker") as string}
                                className="tap-scale w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/25 hover:text-[#e23f5d] hover:bg-[#e23f5d]/10 transition-all"
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M2 4h12M6 4V2h4v2M5 4v8a1 1 0 001 1h4a1 1 0 001-1V4"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Tracker entry form */}
                          {addingEntryFor === tracker.id ? (
                            <div className="px-4 pb-3">
                              <div className="flex items-center gap-2 p-3 rounded-lg bg-[#a33fe8]/[0.06] border border-[#a33fe8]/20">
                                {tracker.dataType === "boolean" ? (
                                  <select
                                    value={entryValue}
                                    onChange={(e) =>
                                      setEntryValue(e.target.value)
                                    }
                                    className="flex-1 px-2.5 py-1.5 text-[16px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-white dark:bg-white/[0.05] text-gray-700 dark:text-white/70 focus:outline-none"
                                    autoFocus
                                  >
                                    <option value="">
                                      {t("dayView.select")}
                                    </option>
                                    <option value="yes">
                                      {t("dayView.yes")}
                                    </option>
                                    <option value="no">
                                      {t("dayView.no")}
                                    </option>
                                  </select>
                                ) : tracker.dataType === "options" &&
                                  tracker.options ? (
                                  <select
                                    value={entryValue}
                                    onChange={(e) =>
                                      setEntryValue(e.target.value)
                                    }
                                    className="flex-1 px-2.5 py-1.5 text-[16px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-white dark:bg-white/[0.05] text-gray-700 dark:text-white/70 focus:outline-none"
                                    autoFocus
                                  >
                                    <option value="">
                                      {t("dayView.selectAnOption")}
                                    </option>
                                    {tracker.options.map((opt, i) => (
                                      <option key={i} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type={
                                      tracker.dataType === "number"
                                        ? "number"
                                        : tracker.dataType === "time"
                                          ? "time"
                                          : "text"
                                    }
                                    value={entryValue}
                                    onChange={(e) =>
                                      setEntryValue(e.target.value)
                                    }
                                    placeholder={`${t("dayView.enterValue", { dataType: tracker.dataType })}${tracker.unit ? ` (${tracker.unit})` : ""}`}
                                    className="flex-1 px-2.5 py-1.5 text-[16px] rounded-lg border border-black/[0.1] dark:border-white/[0.1] bg-white dark:bg-white/[0.05] text-gray-700 dark:text-white/70 focus:outline-none"
                                    autoFocus
                                  />
                                )}
                                <button
                                  onClick={() =>
                                    handleSaveTrackerEntry(tracker.id)
                                  }
                                  disabled={!entryValue.trim()}
                                  className="px-3 py-1.5 text-[16px] font-medium bg-[#f5a623] text-[#1a1300] rounded-lg hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {t("dayView.save")}
                                </button>
                                <button
                                  onClick={handleCancelAddEntry}
                                  className="px-3 py-1.5 text-[16px] font-medium border border-black/[0.1] dark:border-white/[0.1] text-gray-500 dark:text-white/50 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-all"
                                >
                                  {t("dayView.cancel")}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="px-4 pb-3">
                              <button
                                onClick={() =>
                                  handleAddTrackerEntry(tracker.id)
                                }
                                disabled={isFutureDate}
                                className={`text-[15px] font-medium ${isFutureDate ? "text-gray-300 dark:text-white/20 cursor-not-allowed" : "text-[#a33fe8] hover:opacity-80 cursor-pointer"}`}
                              >
                                {isFutureDate
                                  ? t("dayView.cannotAddFuture")
                                  : hasEntries && tracker.dataType === "number"
                                    ? t("dayView.addAnotherEntry")
                                    : !hasEntries
                                      ? t("dayView.addEntry")
                                      : null}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </section>
            )}

            {/* â”€â”€ EMPTY STATE â”€â”€ */}
            {dateData.todos.length === 0 &&
              dateData.habits.filter((h: Habit) => !h.isPaused).length === 0 &&
              dateData.reminders.length === 0 &&
              dateData.notes.length === 0 &&
              dateData.trackers.filter((tr: Tracker) => !tr.isPaused).length ===
                0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="text-5xl mb-4">{"\u{1F4C5}"}</div>
                  <h2 className="text-[19px] font-semibold text-gray-600 dark:text-white/50 mb-1.5">
                    {t("dayView.noActivities")}
                  </h2>
                  <p className="text-[16px] text-gray-400 dark:text-white/30 max-w-xs">
                    {t("dayView.addToGetStarted")}
                  </p>
                </div>
              )}
          </div>
          {/* end scroll area */}
        </main>

        {/* â”€â”€ MOBILE BOTTOM NAV â”€â”€ */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* â”€â”€ CONFIRMATION MODAL â”€â”€ */}
      <ConfirmationModal
        isOpen={isModalOpen}
        message={modalMessage}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />

      {/* â”€â”€ BINGO ANIMATION â”€â”€ */}
      {bingoAnimation?.show && (
        <BingoAnimation
          type={bingoAnimation.type}
          onComplete={() => setBingoAnimation(null)}
        />
      )}
    </>
  );
};

export default DayView;
